'use client';

import { MouseEvent, useEffect, useState } from 'react';
import { Heart } from 'lucide-react';
import { useLocale } from 'next-intl';
import { cn } from '@/lib/utils';
import { FavoriteProduct, useFavoritesStore } from '@/store/favorites-store';

interface FavoriteButtonProps {
  product: FavoriteProduct;
  className?: string;
  showLabel?: boolean;
}

export function FavoriteButton({ product, className, showLabel = false }: FavoriteButtonProps) {
  const locale = useLocale();
  const toggleFavorite = useFavoritesStore((state) => state.toggleFavorite);
  const active = useFavoritesStore((state) => state.items.some((item) => item.id === product.id));
  const [mounted, setMounted] = useState(false);
  const isActive = mounted && active;

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- standaard hydration-mount-detectie; er is geen ander moment om dit te weten dan in een effect
    setMounted(true);
  }, []);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    toggleFavorite(product);
  };
  const copy = {
    nl: { add: 'Toevoegen aan favorieten', remove: 'Verwijder uit favorieten', label: 'Favoriet' },
    de: { add: 'Zu Favoriten hinzufügen', remove: 'Aus Favoriten entfernen', label: 'Favorit' },
    en: { add: 'Add to favorites', remove: 'Remove from favorites', label: 'Favorite' },
    fr: { add: 'Ajouter aux favoris', remove: 'Retirer des favoris', label: 'Favori' },
  }[locale as 'nl' | 'de' | 'en' | 'fr'] || { add: 'Toevoegen aan favorieten', remove: 'Verwijder uit favorieten', label: 'Favoriet' };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        'inline-flex h-10 items-center justify-center gap-2 rounded-full border border-slate-200 bg-white px-3 font-bold text-slate-700 shadow-sm transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700',
        isActive && 'border-rose-200 bg-rose-50 text-rose-700',
        !showLabel && 'w-10 px-0',
        className
      )}
      aria-pressed={isActive}
      aria-label={isActive ? copy.remove : copy.add}
    >
      <Heart className={cn('h-5 w-5', isActive && 'fill-current')} />
      {showLabel && <span>{copy.label}</span>}
    </button>
  );
}
